<template>
  <HermesDrawer
    :model-value="modelValue"
    :title="heading"
    :close-on-scrim="!dirty"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div>
        <h3 class="h-red__title">{{ heading }}</h3>
        <div class="h-red__sub">{{ subheading }}</div>
      </div>
    </template>

    <div class="h-red">
      <div v-if="readOnlyReason" class="h-red__callout">{{ readOnlyReason }}</div>

      <div class="h-red__field">
        <label class="h-red__label" for="h-red-name">Name <span v-if="canEdit" class="h-red__req">*</span></label>
        <input
          id="h-red-name"
          v-model.trim="form.name"
          class="h-red__input"
          type="text"
          maxlength="120"
          autocomplete="off"
          :disabled="!canRename"
        />
        <p v-if="canEdit && !canRename" class="h-red__hint">This role is referred to by name and cannot be renamed.</p>
        <p v-if="fieldError('name')" class="h-red__err">{{ fieldError('name') }}</p>
      </div>

      <div class="h-red__permhead">
        <label class="h-red__label">Permissions</label>
        <span class="h-red__count">{{ form.permissions.length }} of {{ totalPermissions }}</span>
      </div>

      <div v-for="g in groups" :key="g.group" class="h-red__group">
        <div class="h-red__grouphead">
          <span>{{ g.group }}</span>
          <button
            v-if="canEdit"
            type="button"
            class="h-red__link"
            @click="toggleGroup(g)"
          >{{ groupAllOn(g) ? 'Clear' : 'Select all' }}</button>
        </div>
        <label
          v-for="p in g.permissions"
          :key="p.key"
          class="h-red__perm"
          :class="{ on: form.permissions.includes(p.key), 'is-disabled': !canEdit }"
        >
          <input type="checkbox" :value="p.key" v-model="form.permissions" :disabled="!canEdit" />
          <span>{{ p.label }}</span>
        </label>
      </div>
      <p v-if="fieldError('permissions')" class="h-red__err">{{ fieldError('permissions') }}</p>

      <div v-if="otherPermissions.length" class="h-red__callout h-red__callout--quiet">
        <b>Also holds {{ otherPermissions.length }} permission{{ otherPermissions.length === 1 ? '' : 's' }} not listed above.</b>
        These were granted by the system and are not changed from this screen:
        <span class="h-red__mono">{{ otherPermissions.map(shortKey).join(', ') }}</span>
      </div>

      <p v-if="error && !errorField" class="h-red__saveerr">{{ error }}</p>

      <div v-if="canDelete" class="h-red__danger">
        <div>
          <b>Delete this role</b>
          <p>{{ role.user_count ? `Held by ${role.user_count} user${role.user_count === 1 ? '' : 's'}. Remove it from them first.` : 'Nobody holds it. This cannot be undone.' }}</p>
        </div>
        <button type="button" class="h-btn h-btn--danger" :disabled="!!role.user_count || saving" @click="confirmOpen = true">Delete</button>
      </div>
    </div>

    <HermesConfirmModal
      v-model="confirmOpen"
      title="Delete role"
      :description="`Delete the role “${role?.name || ''}”?`"
      confirm-word="DELETE"
      confirm-label="Delete role"
      :danger="true"
      @confirm="remove"
    />

    <template #footer>
      <button type="button" class="h-btn h-btn--ghost" @click="$emit('update:modelValue', false)">{{ canEdit ? 'Cancel' : 'Close' }}</button>
      <button v-if="canEdit" type="button" class="h-btn h-btn--primary" :disabled="saving || !canSubmit" @click="submit">
        <span v-if="saving" class="h-red__spin"></span>
        {{ saving ? 'Saving…' : (isNew ? 'Create role' : 'Save changes') }}
      </button>
    </template>
  </HermesDrawer>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesRoleEditorDrawer — create, edit or just read one role. Opened by
// HermesRolesList. The permission list is the registered catalogue from
// GET /admin/roles (`groups`); a role's permissions are saved as a whole set.
//
// Read-only when the viewer is not a platform operator, and for the platform
// owner role, whose grants are changed by migration so nobody can lock the
// platform out of itself from a screen.
export default {
  name: 'HermesRoleEditorDrawer',
  props: {
    modelValue: { type: Boolean, default: false },
    apiBase: { type: String, default: '/api/v1' },
    // The row being edited, or null for a new role.
    role: { type: Object, default: null },
    // [{ group, permissions: [{ key, label }] }]
    groups: { type: Array, default: () => [] },
    platform: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      api: useApi(),
      form: { name: '', permissions: [] },
      original: { name: '', permissions: [] },
      saving: false,
      error: '',
      errorField: '',
      confirmOpen: false,
    };
  },
  computed: {
    isNew() {
      return !this.role;
    },
    canEdit() {
      return this.isNew ? this.platform : !!this.role.can_edit;
    },
    canRename() {
      return this.isNew ? this.platform : !!this.role.can_rename;
    },
    canDelete() {
      return !this.isNew && !!this.role.can_delete;
    },
    heading() {
      if (this.isNew) return 'New role';
      return this.canEdit ? 'Edit role' : 'Role';
    },
    subheading() {
      if (this.isNew) return 'A named set of permissions to give to users.';
      const n = this.role.user_count || 0;
      return `${this.role.name} · held by ${n} user${n === 1 ? '' : 's'}`;
    },
    readOnlyReason() {
      if (this.isNew || this.canEdit) return '';
      if (!this.platform) return 'Roles are shared by every vendor, so only a platform operator can change them.';
      return 'The platform owner role is changed by migration, not from this screen.';
    },
    otherPermissions() {
      return this.role?.other_permissions || [];
    },
    totalPermissions() {
      return this.groups.reduce((n, g) => n + g.permissions.length, 0);
    },
    permissionsChanged() {
      return [...this.form.permissions].sort().join('|') !== [...this.original.permissions].sort().join('|');
    },
    dirty() {
      return this.canEdit && (this.form.name !== this.original.name || this.permissionsChanged);
    },
    canSubmit() {
      return !!this.form.name && (this.isNew || this.dirty);
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      const state = {
        name: this.role?.name || '',
        permissions: [...(this.role?.permissions || [])],
      };
      this.form = { name: state.name, permissions: [...state.permissions] };
      this.original = state;
      this.saving = false;
      this.error = '';
      this.errorField = '';
      this.confirmOpen = false;
    },
    groupAllOn(g) {
      return g.permissions.every((p) => this.form.permissions.includes(p.key));
    },
    toggleGroup(g) {
      const keys = g.permissions.map((p) => p.key);
      if (this.groupAllOn(g)) {
        this.form.permissions = this.form.permissions.filter((k) => !keys.includes(k));
      } else {
        this.form.permissions = [...new Set([...this.form.permissions, ...keys])];
      }
    },
    // `Some\Enum\Class::CASE` → `Class::CASE`
    shortKey(key) {
      const [cls, name] = String(key).split('::');
      return `${cls.split('\\').pop()}::${name}`;
    },
    fieldError(field) {
      return this.errorField === field ? this.error : '';
    },
    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      this.error = '';
      this.errorField = '';

      try {
        if (this.isNew) {
          await this.api.post(`${this.apiBase}/admin/roles`, {
            name: this.form.name,
            permissions: this.form.permissions,
          });
        } else {
          const payload = {};
          if (this.canRename && this.form.name !== this.original.name) payload.name = this.form.name;
          if (this.permissionsChanged) payload.permissions = this.form.permissions;
          await this.api.patch(`${this.apiBase}/admin/roles/${this.role.id}`, payload);
        }
        this.$emit('saved');
        this.$emit('update:modelValue', false);
      } catch (e) {
        this.error = this.errMsg(e, 'save');
        this.errorField = e?.response?.data?.field || '';
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      if (this.saving) return;
      this.saving = true;
      this.error = '';
      this.errorField = '';
      try {
        await this.api.delete(`${this.apiBase}/admin/roles/${this.role.id}`);
        this.$emit('saved');
        this.$emit('update:modelValue', false);
      } catch (e) {
        this.error = this.errMsg(e, 'delete');
      } finally {
        this.saving = false;
        this.confirmOpen = false;
      }
    },
    errMsg(e, verb) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message;
      if (serverMsg) return serverMsg;
      if (status === 403) return 'You do not have permission to do that.';
      return `Could not ${verb} the role${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
    },
  },
};
</script>

<style scoped>
.h-red { font-family: var(--h-font-body, system-ui); }
.h-red__title { margin: 0; font-size: 15px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-red__sub { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; }

.h-red__field { margin-bottom: 18px; }
.h-red__label { display: block; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); margin-bottom: 6px; }
.h-red__req { color: var(--h-danger, #EF4444); margin-left: 2px; }
.h-red__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 0; line-height: 1.5; }
.h-red__err { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin: 6px 0 0; }
.h-red__link { border: none; background: none; padding: 0; font: inherit; font-size: 12px; color: var(--h-accent-dark, #0369A1); cursor: pointer; text-decoration: underline; }
.h-red__mono { font-family: var(--h-font-mono, ui-monospace, monospace); font-size: 11.5px; word-break: break-word; }

.h-red__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-red__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-red__input:disabled { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B); cursor: not-allowed; }

.h-red__permhead { display: flex; align-items: baseline; justify-content: space-between; }
.h-red__count { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); }
.h-red__group { border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); margin-bottom: 10px; overflow: hidden; }
.h-red__grouphead { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: var(--h-bg-subtle, #F1F5F9); font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--h-text-secondary, #64748B); }
.h-red__perm { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-top: 1px solid var(--h-border, #E2E8F0); cursor: pointer; font-size: 13.5px; color: var(--h-text-primary, #0F172A); }
.h-red__perm.on { background: var(--h-accent-light, #E0F2FE); }
.h-red__perm.is-disabled { cursor: default; }

.h-red__callout { font-size: 12.5px; line-height: 1.5; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); padding: 11px 13px; margin-bottom: 16px; }
.h-red__callout--quiet { margin-top: 14px; margin-bottom: 0; }
.h-red__callout b { color: var(--h-text-primary, #0F172A); }

.h-red__saveerr { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }

.h-red__danger { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 22px; padding: 13px; border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }
.h-red__danger b { font-size: 13px; color: var(--h-text-primary, #0F172A); }
.h-red__danger p { font-size: 12px; color: var(--h-text-secondary, #64748B); margin: 2px 0 0; }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--ghost { background: transparent; color: var(--h-text-secondary, #64748B); }
.h-btn--ghost:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary, #0F172A); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.h-btn--danger { background: var(--h-danger, #EF4444); color: #fff; flex: none; }
.h-btn--danger:disabled { opacity: 0.5; cursor: not-allowed; }
.h-red__spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: h-red-spin 0.7s linear infinite; }
@keyframes h-red-spin { to { transform: rotate(360deg); } }
</style>
